import {
  completionProviders,
  WorkflowCompletionRequirement,
} from '@app/shared/workflow-commands/workflow-completion-requirements';
import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowStatusConfiguration,
  WorkflowStatusRule,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import {
  capabilityIdentity,
  sameCapabilityReference,
} from './workflow-capability-authoring';

export const statusEvents = [
  { code: 'workflow_started', label: 'Process starts', scope: 'workflow' },
  { code: 'workflow_completed', label: 'Process completes', scope: 'workflow' },
  { code: 'step_entered', label: 'Work arrives at the step', scope: 'both' },
  { code: 'step_returned', label: 'Work returns to the step', scope: 'both' },
  { code: 'step_completed', label: 'Step completes', scope: 'both' },
  {
    code: 'step_outcome_followed',
    label: 'Step outcome is followed',
    scope: 'workflow',
  },
  { code: 'form_saved', label: 'Required form is saved', scope: 'both' },
  {
    code: 'document_uploaded',
    label: 'Required document is uploaded',
    scope: 'both',
  },
  {
    code: 'decision_recorded',
    label: 'Required review is recorded',
    scope: 'both',
  },
] as const;

export function emptyStatusConfiguration(): WorkflowStatusConfiguration {
  return {
    statuses: [],
    initialStatusCode: '',
    allowManualChanges: false,
    rules: [],
  };
}

export function statusRequirementProvider(event: string): string | null {
  return (
    (
      {
        form_saved: completionProviders.form,
        document_uploaded: completionProviders.document,
        decision_recorded: completionProviders.decision,
      } as Record<string, string>
    )[event] ?? null
  );
}

export function statusScopeNode(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
): WorkflowDefinitionNode | null {
  if (nodeId === null) return null;
  const matches = document.nodes.filter((n) => n.id === nodeId);
  return matches.length === 1 && matches[0].typeCode === 'human_step'
    ? matches[0]
    : null;
}

export function statusRequirementLabel(
  step: WorkflowDefinitionNode,
  requirement: WorkflowCompletionRequirement,
): string {
  if (requirement.provider === completionProviders.document)
    return String(
      requirement.settings['documentTypeLabel'] || 'Required document',
    );
  if (requirement.provider === completionProviders.form) {
    const sources =
      step.declaration?.behavior.commands.filter(
        (c) => c.captureForm?.id === requirement.settings['formId'],
      ) || [];
    return sources.length === 1
      ? sources[0].captureForm?.title || 'Required form'
      : 'Unavailable form';
  }
  return 'Recorded review';
}

export function statusConfigurationAt(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
): WorkflowStatusConfiguration | null {
  return (
    (nodeId === null
      ? document.statusConfiguration
      : statusScopeNode(document, nodeId)?.statusConfiguration) ?? null
  );
}

/** Outcomes belong to one private result path, rather than to a saved review receipt. */
export function statusStepOutcomeChoices(
  document: WorkflowDefinitionDocument,
  nodeId: string,
): Array<{ code: string; label: string }> {
  const step = statusScopeNode(document, nodeId);
  const commands = step?.declaration?.behavior.commands ?? [];
  const sources = commands.filter((command) => command.outcomes.length);
  const source = sources.length === 1 ? sources[0] : null;
  const pins = source ? document.capabilityReferences?.filter(reference => reference?.key &&
    capabilityIdentity(reference.key) === capabilityIdentity(source.capability.key)) ?? [] : [];
  if (
    !step ||
    sources.length !== 1 ||
    new Set(
      commands.map((command) => capabilityIdentity(command.capability.key)),
    ).size !== commands.length ||
    pins.length !== 1 || !sameCapabilityReference(pins[0], source!.capability)
  )
    return [];
  const entering = document.edges.filter((edge) => edge.fromNodeId === nodeId);
  if (
    entering.length !== 1 ||
    entering[0].outcomeCode != null ||
    entering[0].pathKindCode != null
  )
    return [];
  const routers = document.nodes.filter(
    (node) => node.id === entering[0].toNodeId,
  );
  const router = routers[0];
  if (
    routers.length !== 1 ||
    router.typeCode !== 'routing_decision' ||
    !router.routing?.conditionCode ||
    !router.routing.conditionVersion ||
    document.edges.filter((edge) => edge.toNodeId === router.id).length !== 1
  )
    return [];
  const outcomes = sources[0].outcomes;
  const paths = document.edges.filter((edge) => edge.fromNodeId === router.id);
  if (
    !outcomes.length ||
    outcomes.some((outcome) => !outcome.code) ||
    new Set(outcomes.map((outcome) => outcome.code)).size !== outcomes.length ||
    paths.length !== outcomes.length ||
    new Set(paths.map((path) => path.outcomeCode)).size !== paths.length ||
    paths.some((path) => {
      const targets = document.nodes.filter(
        (node) => node.id === path.toNodeId,
      );
      return (
        !outcomes.some((outcome) => outcome.code === path.outcomeCode) ||
        (path.pathKindCode != null && path.pathKindCode !== 'return') ||
        targets.length !== 1 ||
        !['human_step', 'terminal'].includes(targets[0].typeCode) ||
        (path.pathKindCode === 'return' && targets[0].typeCode !== 'human_step')
      );
    }) ||
    [...entering, ...paths].some(
      (path) =>
        document.edges.filter((edge) => edge.id === path.id).length !== 1,
    )
  )
    return [];
  return outcomes.map((outcome) => ({
    code: outcome.code,
    label: outcome.label || outcome.code,
  }));
}

export function statusRuleError(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
  config: WorkflowStatusConfiguration,
  rule: WorkflowStatusRule,
): string | null {
  const event = statusEvents.find((e) => e.code === rule.eventCode);
  if (!event || (nodeId !== null && event.scope === 'workflow'))
    return 'Choose an event supported by this status scope.';
  if (!config.statuses.some((s) => s.code === rule.statusCode))
    return 'Choose a status defined in this scope.';
  const needsStep = !rule.eventCode.startsWith('workflow_');
  const source = nodeId === null ? (rule.sourceNodeId ?? null) : nodeId;
  const step = source === null ? null : statusScopeNode(document, source);
  if (needsStep && !step) return 'Choose an existing work step for this event.';
  if ((!needsStep || nodeId !== null) && rule.sourceNodeId)
    return 'This rule must use its own status scope.';
  const provider = statusRequirementProvider(rule.eventCode);
  if (provider) {
    const matches =
      step?.declaration?.behavior.completionRequirements?.filter(
        (r) => r.id === rule.requirementId,
      ) ?? [];
    if (matches.length !== 1 || matches[0].provider !== provider)
      return 'Choose the exact requirement for this event.';
  } else if (rule.requirementId)
    return 'This event does not use a requirement.';
  if (rule.eventCode === 'step_outcome_followed') {
    if (
      !rule.outcomeCode ||
      !step ||
      !statusStepOutcomeChoices(document, step.id).some(
        (outcome) => outcome.code === rule.outcomeCode,
      )
    )
      return 'Choose an outcome on this step’s configured paths.';
    return null;
  }
  if (
    rule.outcomeCode &&
    (rule.eventCode !== 'decision_recorded' ||
      !['approved', 'changes_required'].includes(rule.outcomeCode))
  )
    return 'Choose a supported review result.';
  if (rule.outcomeCode) {
    const requirement =
      step?.declaration?.behavior.completionRequirements?.find(
        (r) => r.id === rule.requirementId,
      );
    const outcomes = requirement?.settings['outcomeCodes'];
    if (!Array.isArray(outcomes) || !outcomes.includes(rule.outcomeCode))
      return 'Choose a result supported by this review requirement.';
  }
  return null;
}

export function statusConfigurationError(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
  config: WorkflowStatusConfiguration,
): string | null {
  if (nodeId !== null && !statusScopeNode(document, nodeId))
    return 'This work step is no longer available.';
  if (config.statuses.length > 50)
    return 'Use at most 50 statuses in this scope.';
  if (config.rules.length > 100)
    return 'Use at most 100 automatic changes in this scope.';
  if (!config.statuses.length)
    return config.rules.length
      ? 'Add a status before defining automatic changes.'
      : null;
  const labels = new Set<string>();
  const codes = new Set<string>();
  for (const status of config.statuses) {
    if (!status.code || codes.has(status.code))
      return 'Each status needs a unique identity.';
    if (
      !status.label.trim() ||
      status.label.trim().length > 120 ||
      Array.from(status.label).some(
        (character) =>
          character.charCodeAt(0) < 32 ||
          (character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159),
      )
    )
      return 'Give every status a name of up to 120 characters.';
    const label = status.label.trim().toLocaleLowerCase();
    if (labels.has(label))
      return 'Use a different name for each status in this scope.';
    codes.add(status.code);
    labels.add(label);
  }
  if (!codes.has(config.initialStatusCode))
    return 'Choose the starting status.';
  const ruleIds = new Set<string>();
  const outcomeRules = new Set<string>();
  for (const rule of config.rules) {
    if (!rule.id || ruleIds.has(rule.id))
      return 'Each automatic change needs a unique identity.';
    ruleIds.add(rule.id);
    const error = statusRuleError(document, nodeId, config, rule);
    if (error) return error;
    if (rule.eventCode === 'step_outcome_followed') {
      const key = JSON.stringify([rule.sourceNodeId, rule.outcomeCode]);
      if (outcomeRules.has(key))
        return 'Use one automatic workflow status change for each step outcome.';
      outcomeRules.add(key);
    }
  }
  return null;
}

export function replaceStatusConfiguration(
  document: WorkflowDefinitionDocument,
  nodeId: string | null,
  config: WorkflowStatusConfiguration,
): WorkflowDefinitionDocument {
  const error = statusConfigurationError(document, nodeId, config);
  if (error) throw new Error(error);
  const next = structuredClone(config);
  next.statuses.forEach((s) => (s.label = s.label.trim()));
  const saved = next.statuses.length ? next : null;
  return {
    ...document,
    payloadSchemaVersion: saved
      ? requireWorkflowPayloadVersion(document.payloadSchemaVersion, '1.11')
      : document.payloadSchemaVersion,
    ...(nodeId === null
      ? { statusConfiguration: saved }
      : {
          nodes: document.nodes.map((n) =>
            n.id === nodeId ? { ...n, statusConfiguration: saved } : n,
          ),
        }),
  };
}
